// File: src/pages/PublicShowcase.js
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import { getInitialActivities, generateActivityItem } from '../data/activityData';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroSection = styled.section`
  text-align: center;
  padding: 60px 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  margin: 0 -20px 60px -20px;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
    margin: 0 -20px 40px -20px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 20px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  margin-bottom: 30px;
  opacity: 0.95;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 25px;
  }
`;

const CTAButton = styled(NavLink)`
  display: inline-block;
  background: #ff6b6b;
  color: white;
  padding: 15px 30px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: #ff5252;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
  }
`;

const StatsSection = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-bottom: 80px;
  
  @media (max-width: 768px) {
    margin-bottom: 60px;
  }
`;

const StatCard = styled.div`
  text-align: center;
  padding: 30px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #667eea;
  margin-bottom: 10px;
`;

const StatLabel = styled.div`
  font-size: 1.1rem;
  color: #666;
  font-weight: 500;
`;

const LiveActivitySection = styled.section`
  margin-bottom: 80px;
  
  @media (max-width: 768px) {
    margin-bottom: 60px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 40px;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 30px;
  }
`;

const ActivityFeed = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  height: 500px;
  overflow: hidden;
  position: relative;
`;

const ActivityList = styled.div`
  height: 100%;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const NewActivityIndicator = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(180deg, rgba(102,126,234,0.2) 0%, rgba(102,126,234,0) 100%);
  padding: 10px;
  text-align: center;
  color: #667eea;
  font-weight: 500;
  animation: slideDown 0.3s ease-out;
  
  @keyframes slideDown {
    from { transform: translateY(-100%); }
    to { transform: translateY(0); }
  }
`;

const ActivityItem = styled.div`
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 15px;
  animation: slideIn 0.3s ease-out;
  background: white;
  
  &:first-child {
    border-top: 1px solid #f0f0f0;
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (max-width: 768px) {
    padding: 15px;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const ActivityIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #667eea;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  flex-shrink: 0;
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityText = styled.div`
  font-size: 1rem;
  color: #333;
  margin-bottom: 5px;
`;

const ActivityTime = styled.div`
  font-size: 0.9rem;
  color: #666;
`;


const TestimonialSection = styled.section`
  background: #f8f9fa;
  padding: 60px 0;
  margin: 0 -20px 0 -20px;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
  }
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const TestimonialCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const TestimonialText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #333;
  margin-bottom: 20px;
  font-style: italic;
`;

const TestimonialAuthor = styled.div`
  font-weight: 600;
  color: #667eea;
`;

const TestimonialRole = styled.div`
  font-size: 0.9rem;
  color: #666;
  margin-top: 5px;
`;

const PublicShowcase = ({ user }) => {
  const [recentActivity, setRecentActivity] = useState([]);

  const [showNewIndicator, setShowNewIndicator] = useState(false);

  const addNewActivity = useCallback(() => {
    const newActivity = generateActivityItem();
    setRecentActivity(prev => [
      newActivity,
      ...prev.slice(0, 29) // Keep last 30 items
    ]);
    
    // Show and hide the new activity indicator
    setShowNewIndicator(true);
    setTimeout(() => setShowNewIndicator(false), 1000);
  }, []);

  useEffect(() => {
    // Initialize with some activities
    setRecentActivity(getInitialActivities());

    // Add new activity every few seconds
    const interval = setInterval(() => {
      addNewActivity();
    }, 3000); // Add new activity every 3 seconds

    return () => clearInterval(interval);
  }, [addNewActivity]);

  return (
    <>
      <Container>
        <HeroSection>
          <HeroTitle>The number #1 private showing platform</HeroTitle>
          <HeroSubtitle>
            Listing agents list their properties for free and get free radius marketing for new seller leads, Buyer's Agents book private 30-minute showings for $10, all agents get a free CRM.
          </HeroSubtitle>
        </HeroSection>

        <StatsSection>
          <StatCard>
            <StatNumber>1,247</StatNumber>
            <StatLabel>Showings This Month</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>89</StatNumber>
            <StatLabel>Active Agents</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>456</StatNumber>
            <StatLabel>Properties Listed</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>98%</StatNumber>
            <StatLabel>Satisfaction Rate</StatLabel>
          </StatCard>
        </StatsSection>

        <LiveActivitySection>
          <SectionTitle>Live Activity Feed</SectionTitle>
          <div style={{ textAlign: 'center', marginBottom: '20px', color: '#666' }}>
            Showing real-time activity across Los Angeles and surrounding areas
          </div>
          <ActivityFeed>
            <ActivityList>
              {recentActivity.map((activity) => (
              <ActivityItem key={activity.id}>
                <ActivityIcon>{activity.icon}</ActivityIcon>
                <ActivityContent>
                  <ActivityText>{activity.text}</ActivityText>
                  <ActivityTime>{activity.time}</ActivityTime>
                </ActivityContent>
              </ActivityItem>
            ))}
            </ActivityList>
          </ActivityFeed>
        </LiveActivitySection>

      </Container>

      <TestimonialSection>
        <SectionTitle>Success Stories</SectionTitle>
        <TestimonialGrid>
          <TestimonialCard>
            <TestimonialText>
              "ShowingBooker transformed how I handle tenant-occupied properties. The automated compliance and 24-hour notice system saved me hours every week."
            </TestimonialText>
            <TestimonialAuthor>Sarah Chen</TestimonialAuthor>
            <TestimonialRole>Listing Agent, Coldwell Banker</TestimonialRole>
          </TestimonialCard>
          
          <TestimonialCard>
            <TestimonialText>
              "My clients love the professional booking experience. It shows them I'm tech-savvy and organized, which builds trust from day one."
            </TestimonialText>
            <TestimonialAuthor>Michael Rodriguez</TestimonialAuthor>
            <TestimonialRole>Buyer's Agent, Compass</TestimonialRole>
          </TestimonialCard>
          
          <TestimonialCard>
            <TestimonialText>
              "The $10 booking fee pays for itself with just one deal. The lead generation and professional impression are worth much more than the cost."
            </TestimonialText>
            <TestimonialAuthor>Jennifer Park</TestimonialAuthor>
            <TestimonialRole>Buyer's Agent, Keller Williams</TestimonialRole>
          </TestimonialCard>
        </TestimonialGrid>
      </TestimonialSection>

    </>
  );
};

export default PublicShowcase;